import styles from './SeriveContractBillingOverlayController.module.scss';

import { LinearProgress } from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, useDialogs } from '@work4all/components';

import { useNavigate, useUser } from '@work4all/data';

import { BatchBillingServiceContractsRequest } from '@work4all/models/lib/Classes/BatchBillingServiceContractsRequest.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { BzObjType } from '@work4all/models/lib/Enums/BzObjType.enum';

import { canAddSericeContractsSubscription } from '@work4all/utils/lib/permissions';

import { DISABLE_SERVICE_CONTRACT_BILLING_IDS } from '../../../../../config';
import { MaskContent } from '../../components/MaskContent/MaskContent';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';

import { CalculationAndPaymentTarget } from './components/CalculationAndPaymentTarget';
import { Containment } from './components/Containment';
import { HeaderData } from './components/HeaderData';
import { useBatchBillingServiceContractsMutation } from './hooks/use-batch-billing-service-contracts';
import { useBatchBillingServiceContractsSettings } from './hooks/use-service-contract-billing-settings';
import { useWatchBillingServiceContractsSettingsUpdate } from './hooks/use-watch-billing-service-contracts-settings-update';
import { ServiceContractBillingContextProvider } from './service-contract-billing-context';
import { ServiceContractsBillingsFormValue } from './types';

export const ServiceConfirmationBillingOverlayController = (
  props: MaskControllerProps
) => {
  const { t } = useTranslation();
  const dialogs = useDialogs();
  const navigate = useNavigate();

  const initMask = useMaskConfig(props);
  const mask = { ...initMask, wip: false, isCreateMode: true };

  const maskSelectedIds = useMemo(
    () =>
      mask.params.selectedIds?.length
        ? mask.params.selectedIds?.split(',')
        : [],
    [mask.params.selectedIds]
  );

  const request: DataRequest = {
    entity: null,
    data: null,
  };
  const batchBillingServiceContractsSettings =
    useBatchBillingServiceContractsSettings();
  const defaultBatchBillingRequestData: BatchBillingServiceContractsRequest =
    useMemo(
      () => ({
        codes: maskSelectedIds,
        dueDate: DateTime.now().toISO(),
        invoiceDate: DateTime.now().toISO(),
        accountingDate: DateTime.now().toISO(),
        bzObjType: BzObjType.RECHNUNG,
      }),
      [maskSelectedIds]
    );

  const newEntityData = useMemo(() => {
    return {
      ...defaultBatchBillingRequestData,
      ...batchBillingServiceContractsSettings,
    };
  }, [batchBillingServiceContractsSettings, defaultBatchBillingRequestData]);

  const normalizeData = useCallback(
    (_entity: null, newEntity: ServiceContractsBillingsFormValue) => ({
      ...newEntity,
    }),
    []
  );

  const overlay = useMaskOverlay<ServiceContractsBillingsFormValue>({
    ...props,
    request,
    newEntityData,
    mask,
    getSubTitle: (x) => x.batchBillingRunName,
    normalizeData,
  });

  const user = useUser();
  const hasServiceContractMaintenanceRights =
    canAddSericeContractsSubscription(user);
  const entityRights = useMemo(
    () => ({
      create: hasServiceContractMaintenanceRights,
      read: hasServiceContractMaintenanceRights,
      update: hasServiceContractMaintenanceRights,
      delete: hasServiceContractMaintenanceRights,
    }),
    [hasServiceContractMaintenanceRights]
  );

  useWatchBillingServiceContractsSettingsUpdate({
    form: overlay.form,
    defaultValues: batchBillingServiceContractsSettings,
  });

  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [
    numberOfServiceContractsToProcess,
    setNumberOfServiceContractsToProcess,
  ] = useState(maskSelectedIds?.length ?? 0);
  const [allSelected, setAllSelected] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [mutateBatchBillingServiceContracts] =
    useBatchBillingServiceContractsMutation({
      onCompleted(data) {
        setProgressDialogOpen(false);

        const errors = data.batchBillingServiceContracts.errors?.data;

        dialogs.confirm({
          title: t('MASK.BILLING.SUCCESS_DIALOG.TITLE'),
          description: errors
            ? t('MASK.BILLING.SUCCESS_DIALOG.DESCRIPTION_ERROR')
            : t('MASK.BILLING.SUCCESS_DIALOG.DESCRIPTION_OK', {
                count: numberOfServiceContractsToProcess,
              }),
          confirmLabel: t('MASK.BILLING.SUCCESS_DIALOG.BTN_NAVIGATE'),
          cancelLabel: t('ALERTS.CLOSE'),
          onConfirm: () => {
            overlay.form.reset(overlay.initialFormValue.value);
            props.onAfterSave({});
            navigate('/more/entity/invoice');
          },
          onCancel: () => {
            props.onAfterSave({ shouldRefetch: true });
          },
        });
      },
    });

  const onSubmit = useCallback(
    async (formData: ServiceContractsBillingsFormValue) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const confirmed = await dialogs.confirm({
          title: t('MASK.BILLING.CONFIRM_DIALOG.TITLE'),
          description: t('MASK.BILLING.CONFIRM_DIALOG.DESCRIPTION', {
            count: numberOfServiceContractsToProcess,
          }),
          confirmLabel: t('MASK.BILLING.CONFIRM_DIALOG.BTN_CONFIRM'),
          cancelLabel: t('ALERTS.BTN_ABORT'),
        });

        if (confirmed) {
          const toModify = pickUpdateFields(
            formData,
            overlay.form.formState.dirtyFields
          );

          setProgressDialogOpen(true);

          await mutateBatchBillingServiceContracts({
            variables: {
              setting: {
                codes: allSelected
                  ? defaultBatchBillingRequestData.codes.map(Number)
                  : null,
                dueDate: !allSelected
                  ? toModify?.dueDate || defaultBatchBillingRequestData.dueDate
                  : null,
                invoiceDate:
                  toModify?.invoiceDate ??
                  defaultBatchBillingRequestData.invoiceDate,
                accountingDate:
                  toModify?.accountingDate ??
                  defaultBatchBillingRequestData.accountingDate,
                bzObjType:
                  toModify?.bzObjType ??
                  defaultBatchBillingRequestData.bzObjType,
              },
            },
          });
        } else {
          return;
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      allSelected,
      defaultBatchBillingRequestData.accountingDate,
      defaultBatchBillingRequestData.bzObjType,
      defaultBatchBillingRequestData.codes,
      defaultBatchBillingRequestData.dueDate,
      defaultBatchBillingRequestData.invoiceDate,
      dialogs,
      isSubmitting,
      mutateBatchBillingServiceContracts,
      numberOfServiceContractsToProcess,
      overlay.form.formState.dirtyFields,
      t,
    ]
  );
  const disabled =
    !DISABLE_SERVICE_CONTRACT_BILLING_IDS.includes(user.kundennummer) ||
    !numberOfServiceContractsToProcess;

  return (
    <ServiceContractBillingContextProvider
      value={{
        allSelected,
        setAllSelected,
        numberOfServiceContractsToProcess,
        setNumberOfServiceContractsToProcess,
      }}
    >
      <OverlayController<ServiceContractsBillingsFormValue>
        {...overlay}
        title={t('MASK.BILLING')}
        subTitle={t('COMMON.SERVICECONTRACT')}
        submitButtonProps={{
          ...overlay.submitButtonProps,
          title: t('MASK.CREATE_BILLING'),
          disabled,
          disableReason: !DISABLE_SERVICE_CONTRACT_BILLING_IDS.includes(
            user.kundennummer
          )
            ? t('MASK.BILLING.DISABLE_REASON')
            : undefined,
          showIcon: false,
          isDirty: true,
        }}
        showFullscreen={false}
        onSubmit={onSubmit}
        entityRights={entityRights}
        isDirty={true}
      >
        <MaskContent className={styles.content}>
          <Containment />
          <HeaderData />
          <CalculationAndPaymentTarget />
        </MaskContent>
      </OverlayController>

      <Dialog
        title={t('MASK.BILLING.PROGRESS_DIALOG.TITLE')}
        open={progressDialogOpen}
        closeButton={false}
      >
        <LinearProgress sx={{ margin: 2 }} />
      </Dialog>
    </ServiceContractBillingContextProvider>
  );
};
